import {
    EPlaybookFilter,
    EPlaybookStepTypeGroup,
    EPlaybookTrigger,
    EPlaybookTriggerFilter,
    type IPlaybookActionStep,
    type IPlaybookStep,
    type IPlaybookV2,
} from 'src/services/api/playbooks/types';

/**
 * Flatten the step tree (depth-first, pre-order).
 * The legacy flat playbook shape (`primary_action`, `lead_filters`, `lead_filter_params`)
 * is gone from the API, so everything the UI used to read off it is derived from here.
 */
export const flattenPlaybookSteps = (firstStep?: IPlaybookStep): IPlaybookStep[] => {
    if (!firstStep) {
        return [];
    }

    return [
        firstStep,
        ...(firstStep.children ?? []).flatMap((child) => flattenPlaybookSteps(child.step)),
    ];
};

/**
 * The action the playbook performs, i.e. the legacy `primary_action`.
 */
export const getPlaybookActionStep = (firstStep?: IPlaybookStep): IPlaybookActionStep | undefined => {
    return flattenPlaybookSteps(firstStep)
        .find((step): step is IPlaybookActionStep => step.type_group === EPlaybookStepTypeGroup.Action);
};

/**
 * The property this playbook is scoped to, i.e. the legacy `lead_filter_params.property_id`.
 */
export const getPlaybookPropertyId = (firstStep?: IPlaybookStep): number | undefined => {
    for (const step of flattenPlaybookSteps(firstStep)) {
        const params = step.params as { property_id?: number } | null;
        if (
            (step.type === EPlaybookFilter.SpecificProperty || step.type === EPlaybookTriggerFilter.SpecificProperty) &&
            params?.property_id != null
        ) {
            return params.property_id;
        }
    }
};

/**
 * The legacy `is_background` flag: everything that is not manually triggered.
 */
export const isBackgroundPlaybook = (playbook: IPlaybookV2): boolean => {
    return playbook.trigger.type !== EPlaybookTrigger.Manual;
};
